import React from 'react';
import {
  type ShortCode,
  type EnrichmentContext,
  type TransformedItemPost,
  fetchEmbeddedPostsByCat,
  withDefaultContributors,
} from '@highereducation/wordpress';
import { CategoryHubSection } from '@highereducation/hen-ui';
import type { ArticleCardProps } from '@highereducation/hen-ui';
import { parseShowImage, parseTruthy, toArticleCardProps } from '../utils';

const DEFAULT_FETCH_LIMIT = '1000';
/** Hub sections always show newest published posts first (matches leaf hub expectation). */
const HUB_POST_ORDER = 'published' as const;
const DEFAULT_CARD_SIZE = 'compact' as const;
const DEFAULT_CARD_BORDER = 'stroke' as const;
const DEFAULT_SHOW_IMAGE = false;
const HUB_CARD_VARIANT = 'byline' as const;

interface CategoryHubShortcodeProps {
  category?: string;
  'view-all-link'?: string;
  href?: string;
  /** When `"true"`, shows inline Load more instead of View all. Default: View all only. */
  'show-load-more'?: string | boolean;
  size?: ArticleCardProps['size'];
  'show-image'?: string | boolean;
  'show-brow'?: string | boolean;
  'card-brow'?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * CategoryHubShortcode — parent hub section matching auto category hubs.
 *
 * Nest `[hen-ui-heading]` as the only child so the heading shares a row with
 * count and **View all** (same layout as the dynamic hub).
 *
 * @example
 * [hen-ui-category-hub category="addiction-work" view-all-link="/addiction-work/" card-brow="Careers"]
 *   [hen-ui-heading variant="h3"]Addiction Work[/hen-ui-heading]
 * [/hen-ui-category-hub]
 */
export const CategoryHubShortcode: ShortCode<
  CategoryHubShortcodeProps,
  TransformedItemPost[]
> = {
  balanced: true,
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<TransformedItemPost[]> => {
    const attrs = context?.attrs ?? {};
    const config = context?.config;
    if (!config?.cmsUrl || !config?.postTypeGql) return [];

    const category = attrs.category?.trim();
    if (!category) return [];

    const result = await fetchEmbeddedPostsByCat(
      [category],
      { limit: DEFAULT_FETCH_LIMIT, orderBy: HUB_POST_ORDER },
      config.postTypeGql,
      config.cmsUrl,
    );
    return withDefaultContributors(result ?? [], config);
  },

  component: ({
    category,
    'view-all-link': viewAllLink,
    href: legacyHref,
    size,
    'show-image': showImageAttr,
    'show-brow': showBrowAttr,
    'card-brow': cardBrow,
    'show-load-more': showLoadMoreAttr,
    children,
    enrichments,
  }) => {
    const posts = (enrichments?.data as TransformedItemPost[] | null) ?? [];
    const viewAllHref =
      viewAllLink ?? legacyHref ?? (category ? `/${category}/` : '#');

    const showImage =
      showImageAttr === undefined
        ? DEFAULT_SHOW_IMAGE
        : parseShowImage(showImageAttr);
    const showBrow = parseShowImage(showBrowAttr);
    const showLoadMore =
      typeof showLoadMoreAttr === 'boolean'
        ? showLoadMoreAttr
        : parseTruthy(
            typeof showLoadMoreAttr === 'string' ? showLoadMoreAttr : undefined,
          );

    const articles = posts.map((post) => ({
      ...toArticleCardProps(post, { variant: HUB_CARD_VARIANT }),
      layout: 'stacked' as const,
      size: size ?? DEFAULT_CARD_SIZE,
      border: DEFAULT_CARD_BORDER,
      showImage,
      brow: showBrow ? (cardBrow ?? post.brow) : undefined,
    }));

    return (
      <CategoryHubSection
        heading={children}
        sectionId={category?.trim() || 'category-hub'}
        articles={articles}
        viewAllHref={viewAllHref}
        showLoadMore={showLoadMore}
      />
    );
  },
};
